import { useContext, useEffect, useRef, useState } from 'react';
import { WebApiContext } from 'ascendon-web-api';
import { translateFault, generateUnhandledFault } from '../../helpers/faults';
import {
  defaultInvalidRedemptionSubCode,
  invalidRedemptionSubCodeList,
} from '../../constants/redemptionSubCodes.constant';

function useCalculateOrderQuote() {
  const [fault, setFault] = useState(null);
  const [faultCode, setFaultCode] = useState(null);
  const [uiModel, setUiModel] = useState(null);
  const WebApi = useContext(WebApiContext);

  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const requestCalculateOrderQuote = (newRequestData) => {
    const {
      ShoppingCart,
      paymentInstruments = [],
      redemptionCodesPayload,
      UseDefaults,
      isUpgrade,
      SubscriptionId,
      SubscriptionItemId,
    } = newRequestData;
    if (isUpgrade && !paymentInstruments.length) {
      return;
    }
    const getOrderQuoteRequest = (isUpgradePlan) => {
      let paymentInstrumentIds = [];
      let freeOrHasPaymentInstrument;
      let translatedCart = { Items: [] };

      if (paymentInstruments?.length) {
        paymentInstrumentIds = paymentInstruments.map((pi) => pi.Id);
        freeOrHasPaymentInstrument = true;
      } else {
        freeOrHasPaymentInstrument = false;
      }

      translatedCart.Items = ShoppingCart.Items.map((cartItem) => {
        const item = {};
        item.Id = cartItem.Id;
        item.ProductId = cartItem.ProductId;
        item.PricingPlanId = cartItem.PricingPlanId;
        item.Quantity = 1;
        return item;
      });

      if (isUpgradePlan) {
        const getItems = ShoppingCart.Items.map((cartItem) => {
          const item = {};
          item.SubscriptionItemId = SubscriptionItemId;
          item.ProductId = cartItem.ProductId;
          item.PricingPlanId = cartItem.PricingPlanId;
          return item;
        });
        return {
          ReplaceItems: getItems,
          SubscriptionId,
          ReturnSubscriptionPreviews: true,
          PaymentInstrumentIds: paymentInstrumentIds || [],
          RedemptionCodes: redemptionCodesPayload,
          CalculateTaxes: freeOrHasPaymentInstrument,
          CalculateShipping: false,
          ChangeImmediately: true,
          UseDefaults,
        };
      }

      return {
        ShoppingCart: translatedCart,
        ReturnSubscriptionPreviews: true,
        PaymentInstrumentIds: paymentInstrumentIds || [],
        RedemptionCodes: redemptionCodesPayload,
        CalculateTaxes: freeOrHasPaymentInstrument,
        CalculateShipping: false,
        UseDefaults,
      };
    };

    const isUpgradeOrder = isUpgrade
      ? 'CalculateModifyOrderQuote'
      : 'CalculateOrderQuote';

    WebApi.subscriber({
      callName: isUpgradeOrder,
      keyFactors: [isUpgradeOrder],
      forceRefresh: true,
      payload: getOrderQuoteRequest(isUpgrade),
    })
      .then((response) => {
        if (isMounted.current) {
          setFault(null);
          const orderQuote = response.data;
          const { InvalidCouponRedemptions } = response.data;
          if (InvalidCouponRedemptions?.length) {
            const findInvalidRedemptionSubcode =
              invalidRedemptionSubCodeList.find(
                (item) => item === InvalidCouponRedemptions[0]?.SubCode,
              ) || defaultInvalidRedemptionSubCode;
            const errorMsg = translateFault({
              Code: `coupon${findInvalidRedemptionSubcode}`,
            }).translatedMessage;
            setFault('');
            setFault(errorMsg);
            setFaultCode(findInvalidRedemptionSubcode);
          } else {
            setUiModel({
              orderQuote,
            });
          }
        }
      })
      .catch((error) => {
        console.warn({ error });
        const code = error?.data?.Code;
        let errorMsg;
        setFault('');
        if (!code) {
          const unknownFault = generateUnhandledFault(error.data);
          errorMsg = translateFault(unknownFault).translatedMessage;
          setFault(errorMsg);
        } else {
          errorMsg = translateFault(error.data).translatedMessage;
          setFaultCode(code);
          setFault(errorMsg);
        }
      });
  };

  return [uiModel, fault, faultCode, requestCalculateOrderQuote];
}

export { useCalculateOrderQuote };
export default useCalculateOrderQuote;
